import { useEffect, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Navbar, { NAVBAR_HEIGHT } from './Navbar'
import Sidebar, { SIDEBAR_WIDTH } from './Sidebar'

interface Props {
    children: ReactNode
    withSidebar?: boolean
}

const SIDEBAR_COLLAPSED_KEY = 'condogest_sidebar_collapsed'

export default function AppLayout({ children, withSidebar = true }: Props) {
    const [mobileOpen, setMobileOpen] = useState(false)
    // Persistido para que a preferência sobreviva a um refresh/nova navegação
    // de topo (AppLayout não remonta ao navegar dentro do mesmo condomínio).
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    )

    useEffect(() => {
        setMobileOpen(false)
    }, [location.pathname])

    const toggleSidebarCollapsed = () => {
        setSidebarCollapsed((prev) => {
            const next = !prev
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
            return next
        })
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Navbar
                showMenuButton={withSidebar}
                mobileOpen={mobileOpen}
                onMenuClick={() => setMobileOpen((prev) => !prev)}
            />

            {withSidebar && (
                <Sidebar
                    mobileOpen={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={toggleSidebarCollapsed}
                />
            )}

            <Box
                component="main"
                sx={{
                    ml: withSidebar ? { xs: 0, md: sidebarCollapsed ? 0 : `${SIDEBAR_WIDTH}px` } : 0,
                    transition: (theme) =>
                        theme.transitions.create('margin', {
                            easing: sidebarCollapsed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                            duration: sidebarCollapsed ? theme.transitions.duration.leavingScreen : theme.transitions.duration.enteringScreen,
                        }),
                    // Margem inferior mínima + safe-area do dispositivo (barra de
                    // gestos/endereço em iOS/Android e em PWA instalada), para que
                    // o último elemento interativo de cada página (ex: botão
                    // "Guardar" no fundo de um formulário) nunca fique colado à
                    // borda do ecrã ou escondido atrás da barra do browser.
                    pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
                }}
            >
                {/* Espaçador para compensar a Navbar fixa */}
                <Toolbar sx={{ minHeight: `${NAVBAR_HEIGHT}px !important` }} />
                {children}
            </Box>
        </Box>
    )
}
